// Spark Sound Generator — Web Audio API + Speech Synthesis
// "tchinnn + spark digital + whoosh + voz 'DEU SPARK!'"
// Curto, elegante, premium, futurista

let audioCtx = null;

const getCtx = () => {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

// Try to pick a punchy, modern-sounding pt-BR voice if available
const pickVoice = () => {
  if (!('speechSynthesis' in window)) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;
  // Prefer pt-BR > pt > en-US
  const pt = voices.find(v => /pt[-_]?br/i.test(v.lang)) ||
             voices.find(v => /^pt/i.test(v.lang)) ||
             voices.find(v => /en[-_]?us/i.test(v.lang)) ||
             voices[0];
  return pt;
};

const speakDeuSpark = () => {
  if (!('speechSynthesis' in window)) return;
  try {
    // Cancel any previous utterance
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance('Deu Spark!');
    const v = pickVoice();
    if (v) utter.voice = v;
    utter.lang = (v && v.lang) || 'pt-BR';
    utter.rate = 1.05;       // slightly snappy
    utter.pitch = 1.15;      // bright, energetic
    utter.volume = 0.95;
    window.speechSynthesis.speak(utter);
  } catch (e) {
    // ignore
  }
};

// Pre-warm voices list (Chrome populates async)
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  // Force the engine to load voices
  window.speechSynthesis.getVoices();
  window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
}

export const playSparkSound = async () => {
  const ctx = getCtx();
  if (!ctx) return;

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.35;
  master.connect(ctx.destination);

  // ============ Layer 1: Whoosh (low filtered noise) ============
  // Quick rising whoosh that announces the moment.
  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.45, ctx.sampleRate);
  const noiseData = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * 0.5;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.Q.value = 1.2;
  noiseFilter.frequency.setValueAtTime(400, now);
  noiseFilter.frequency.exponentialRampToValueAtTime(2600, now + 0.35);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.0001, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.35, now + 0.08);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(master);
  noise.start(now);
  noise.stop(now + 0.45);

  // ============ Layer 2: Tchinn! (the spark itself — bright bell) ============
  // Two-osc bell using sine + triangle, fast attack, long-ish decay.
  const bellTime = now + 0.18;
  const playBell = (freq, gainPeak, delay = 0, dur = 1.2) => {
    const t = bellTime + delay;
    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(freq, t);
    const o2 = ctx.createOscillator();
    o2.type = 'triangle';
    o2.frequency.setValueAtTime(freq * 2.01, t);
    o2.detune.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gainPeak, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o1.connect(g);
    o2.connect(g);
    g.connect(master);
    o1.start(t);
    o2.start(t);
    o1.stop(t + dur + 0.1);
    o2.stop(t + dur + 0.1);
  };

  // Major-add9 chord ringing: C6, E6, G6, D7 — feels triumphant/hopeful.
  playBell(1046.5, 0.32, 0.0, 1.2);   // C6
  playBell(1318.5, 0.28, 0.02, 1.1);  // E6
  playBell(1567.98, 0.22, 0.04, 1.0); // G6
  playBell(2349.32, 0.18, 0.10, 0.85); // D7 (the sparkle on top)

  // ============ Layer 3: Digital sparkle (FM-like ping) ============
  const sparkleTime = now + 0.28;
  const carrier = ctx.createOscillator();
  const modulator = ctx.createOscillator();
  const modGain = ctx.createGain();
  const sparkleGain = ctx.createGain();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(2800, sparkleTime);
  carrier.frequency.exponentialRampToValueAtTime(4400, sparkleTime + 0.18);
  modulator.type = 'sine';
  modulator.frequency.setValueAtTime(600, sparkleTime);
  modGain.gain.setValueAtTime(700, sparkleTime);
  modGain.gain.exponentialRampToValueAtTime(40, sparkleTime + 0.3);
  modulator.connect(modGain);
  modGain.connect(carrier.frequency);
  sparkleGain.gain.setValueAtTime(0.0001, sparkleTime);
  sparkleGain.gain.exponentialRampToValueAtTime(0.18, sparkleTime + 0.01);
  sparkleGain.gain.exponentialRampToValueAtTime(0.0001, sparkleTime + 0.4);
  carrier.connect(sparkleGain);
  sparkleGain.connect(master);
  modulator.start(sparkleTime);
  carrier.start(sparkleTime);
  modulator.stop(sparkleTime + 0.45);
  carrier.stop(sparkleTime + 0.45);

  // ============ Layer 4: Sub thump (warm low body) ============
  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(120, now + 0.18);
  sub.frequency.exponentialRampToValueAtTime(55, now + 0.45);
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.0001, now + 0.18);
  subGain.gain.exponentialRampToValueAtTime(0.4, now + 0.2);
  subGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
  sub.connect(subGain);
  subGain.connect(master);
  sub.start(now + 0.18);
  sub.stop(now + 0.6);

  // ============ Layer 5: Voice "Deu Spark!" (after the bell rings) ============
  // Speak slightly delayed so it follows the sparkle.
  setTimeout(() => {
    speakDeuSpark();
  }, 550);
};

// Simple notification ping (lighter, no whoosh)
export const playNotificationSound = async () => {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.25;
  master.connect(ctx.destination);

  const notes = [880, 1318.5];
  notes.forEach((freq, i) => {
    const t = now + i * 0.07;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.4, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.connect(g);
    g.connect(master);
    o.start(t);
    o.stop(t + 0.4);
  });
};
